import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

const ThemeContext = createContext();

// Routes where dark mode is supported (light-background pages)
// These are pages with light backgrounds where dark mode improves UX
export const DARK_MODE_ENABLED_ROUTES = [
  '/shop',
  '/dashboard',
  '/payment',
  '/notifications',
  '/admin',
];

// Check if a pathname supports dark mode
export const isDarkModeSupported = (pathname) => {
  return DARK_MODE_ENABLED_ROUTES.some(route => pathname.startsWith(route));
};

// Light theme configuration
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0c4a6e',
      light: '#0ea5e9',
      dark: '#082f49',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0ea5e9',
      light: '#38bdf8',
      dark: '#0284c7',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a',
      secondary: '#64748b',
    },
    divider: '#e2e8f0',
    success: {
      main: '#22c55e',
      light: '#4ade80',
      dark: '#16a34a',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    info: {
      main: '#0284c7',
      light: '#38bdf8',
      dark: '#0369a1',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

// Dark theme configuration
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#0ea5e9',
      light: '#38bdf8',
      dark: '#0284c7',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#38bdf8',
      light: '#7dd3fc',
      dark: '#0ea5e9',
    },
    background: {
      default: '#0f172a',
      paper: '#1e293b',
    },
    text: {
      primary: '#f1f5f9',
      secondary: '#94a3b8',
    },
    divider: '#334155',
    success: {
      main: '#22c55e',
      light: '#4ade80',
      dark: '#16a34a',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    info: {
      main: '#38bdf8',
      light: '#7dd3fc',
      dark: '#0ea5e9',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

export const ThemeProvider = ({ children }) => {
  // Single global dark mode preference - consistent across all supported pages
  const [darkModeEnabled, setDarkModeEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem('darkModeEnabled');
      return saved === 'true';
    } catch (e) {
      console.error('Error loading dark mode preference:', e);
    }
    return false;
  });

  // Track current route for checking if dark mode is supported
  const [currentRoute, setCurrentRoute] = useState(null);

  // Track if dark mode is currently active (route supports it + user enabled it)
  const [isDarkModeActive, setIsDarkModeActive] = useState(false);

  // Persist dark mode preference to localStorage
  useEffect(() => {
    localStorage.setItem('darkModeEnabled', String(darkModeEnabled));
  }, [darkModeEnabled]);

  // Toggle dark mode globally (applies to all supported routes)
  const toggleTheme = useCallback(() => {
    setDarkModeEnabled(prev => !prev);
  }, []);

  // Set specific theme mode globally
  const setTheme = useCallback((mode) => {
    if (mode !== 'light' && mode !== 'dark') return;
    setDarkModeEnabled(mode === 'dark');
  }, []);

  // Apply dark mode based on current route - called by ThemeApplier component
  const applyThemeForRoute = useCallback((pathname) => {
    setCurrentRoute(pathname);
    
    const routeSupports = isDarkModeSupported(pathname);
    const shouldApplyDark = routeSupports && darkModeEnabled;
    
    if (shouldApplyDark) {
      document.documentElement.setAttribute('data-theme', 'dark');
      setIsDarkModeActive(true);
    } else {
      document.documentElement.setAttribute('data-theme', 'light');
      setIsDarkModeActive(false);
    }
  }, [darkModeEnabled]);

  // Re-apply theme when darkModeEnabled changes
  useEffect(() => {
    if (currentRoute) {
      const routeSupports = isDarkModeSupported(currentRoute);
      const shouldApplyDark = routeSupports && darkModeEnabled;
      
      if (shouldApplyDark) {
        document.documentElement.setAttribute('data-theme', 'dark');
        setIsDarkModeActive(true);
      } else {
        document.documentElement.setAttribute('data-theme', 'light');
        setIsDarkModeActive(false);
      }
    }
  }, [darkModeEnabled, currentRoute]);

  // Get the current MUI theme object - only use dark theme when actually active
  const theme = useMemo(() => {
    return isDarkModeActive ? darkTheme : lightTheme;
  }, [isDarkModeActive]);

  const value = {
    darkModeEnabled,              // User's global preference
    isDarkMode: darkModeEnabled,  // User prefers dark mode (global setting)
    isDarkModeActive,             // Dark mode is actually applied (route supports + preference)
    toggleTheme,                  // Toggle dark mode globally
    setTheme,                     // Set theme mode globally
    applyThemeForRoute,           // Function to apply theme based on route
    isDarkModeSupported,          // Function to check if route supports dark mode
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
